import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@packages/utils';
import { TCheckboxChangeEvent } from 'components/Base/Checkbox/Checbox.types';
import Checkbox from 'components/Base/Checkbox';
import EditableSelect from 'components/Base/EditableFields/EditableSelect';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import CreateOrganizationModal from 'components/Shared/CreateOrganizationModal';
import {
  useEditShipmentLocationMutation,
  useGetWorkshopLocationOptionsQuery,
} from 'redux/apiSlice/workshops.slice';
import { TRtkErrorType } from 'data/types/generic.types';
import { TCreateOrganizationModalOpen } from 'components/Shared/CreateOrganizationModal/CreateOrganizationModal.types';
import { Flex } from 'antd';
import { GlobeOutline } from 'assets/icon/icon';
import { Text } from 'components/Base/Typography';
import { TShippingAddressProps } from './ShippingAddress.types';

const ShippingAddress: FC<TShippingAddressProps> = ({
  shipmentLocation,
  sameAsLocation,
  locationId,
  id,
  isFetchingWorkshopData,
}) => {
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.workshops.details.basic-info.shipping-address',
  });
  const { showToastNotification } = useShowToastNotification();
  const [checkboxValue, setCheckboxValue] = useState<boolean>();
  const [open, setOpen] = useState<TCreateOrganizationModalOpen>({
    visible: false,
    initialOrgName: '',
  });
  const { data: organizations } = useGetWorkshopLocationOptionsQuery();
  const [editShipmentLocation, { isLoading: editShipmentLocationLoading }] =
    useEditShipmentLocationMutation();
  const [isSelectActive, setIsSelectActive] = useState(false);
  const selectWrapperRef = useRef<HTMLDivElement>(null);
  const isReadonly =
    shipmentLocation?.allowedOperations?.readonly || checkboxValue;

  useEffect(() => {
    if (!isFetchingWorkshopData && sameAsLocation !== undefined) {
      setCheckboxValue(sameAsLocation);
    }
  }, [isFetchingWorkshopData]);

  useEffect(() => {
    if (!isSelectActive) return undefined;
    const interval = setInterval(() => {
      if (
        selectWrapperRef.current &&
        !selectWrapperRef.current.querySelector('.editable-input')
      ) {
        setIsSelectActive(false);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [isSelectActive]);

  const onShipmentLocationEdit = async (value: string) => {
    setIsSelectActive(false);
    try {
      await editShipmentLocation({
        id,
        value,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (e) {
      showToastNotification({
        message: (e as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  const onCheckboxChange = async (e: TCheckboxChangeEvent) => {
    const value = e.target.checked;
    setCheckboxValue(value);
    if (value && locationId) {
      await onShipmentLocationEdit(locationId);
    }
  };

  return (
    <Flex align="flex-start" gap={6}>
      <Flex
        align="center"
        gap={8}
        style={{ width: 160, marginTop: 8, flexShrink: 0 }}
      >
        <GlobeOutline size={24} />
        <Text color="#808080">{t('LABEL')}</Text>
      </Flex>
      <Flex align="center" gap={12} style={{ flex: 1 }}>
        <div
          ref={selectWrapperRef}
          role="presentation"
          onMouseDown={() => !isReadonly && setIsSelectActive(true)}
        >
          <EditableSelect
            onEdit={onShipmentLocationEdit}
            title={{
              label: shipmentLocation?.nameAndAddress || '',
              value: shipmentLocation?.id || '',
            }}
            isLoading={editShipmentLocationLoading}
            options={organizations}
            addNewOption={{
              onClick: currentTypedValue => {
                setOpen({
                  visible: true,
                  initialOrgName: currentTypedValue || '',
                });
              },
              addButtonLabel: t('ADD'),
            }}
            emptyText={t('EMPTY_TEXT')}
            readonly={isReadonly}
            canRemove={shipmentLocation?.allowedOperations?.canRemove}
            selectWidth={500}
          />
        </div>
        <div
          style={{
            flexShrink: 0,
            opacity: isSelectActive ? 0 : 1,
            pointerEvents: isSelectActive ? 'none' : 'auto',
            transition: 'opacity 0.15s ease',
          }}
        >
          <Checkbox
            onChange={onCheckboxChange}
            checked={checkboxValue}
            disabled={
              editShipmentLocationLoading ||
              shipmentLocation?.allowedOperations?.readonly
            }
            $textNoWrap
            $readonly={shipmentLocation?.allowedOperations?.readonly}
          >
            {t('SAME_AS_LOCATION')}
          </Checkbox>
        </div>
      </Flex>

      <CreateOrganizationModal
        open={open}
        setOpen={setOpen}
        attachOrganization={onShipmentLocationEdit}
        attachOrganizationLoading={editShipmentLocationLoading}
      />
    </Flex>
  );
};

export default ShippingAddress;
